import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Users, Clock, Home, Layers, ArrowRight, CheckCircle2, RefreshCw, X, DollarSign, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
const data = [{
  name: 'The Lawson',
  buyers: 45
}, {
  name: 'The Samson',
  buyers: 32
}, {
  name: 'The Pine',
  buyers: 28
}, {
  name: 'The Cedar',
  buyers: 19
}, {
  name: 'The Birch',
  buyers: 12
}];
const projects = [{
  id: 1,
  name: 'The Lawson'
}, {
  id: 2,
  name: 'The Samson'
}];
const COLORS = ['#06b6d4', '#94a3b8', '#94a3b8', '#94a3b8', '#94a3b8'];
// Upgrade stats
const upgradeStats = {
  totalUpgradeRevenue: 271300,
  avgUpgradeValue: 45217,
  topUpgradeCategory: 'Kitchen'
};
export function DashboardPage() {
  const navigate = useNavigate();
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showProjectModal) {
        setShowProjectModal(false);
        setSelectedProject('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showProjectModal]);
  const handleManageOptions = () => {
    if (!selectedProject) return;
    navigate('/options/models');
    setShowProjectModal(false);
    setSelectedProject('');
  };
  return <>
      <div className="space-y-8 max-w-7xl mx-auto" data-id="element-4805">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-4806">
          <div data-id="element-4807">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-4808">Dashboard</h1>
            <p className="text-slate-500 mt-1" data-id="element-4809">
              Welcome back, here's what's happening today.
            </p>
          </div>
          <div className="flex gap-3" data-id="element-4810">
            <Button variant="outline" onClick={() => navigate('/analytics')} data-id="element-4811">
              View Analytics
            </Button>
            <Button onClick={() => setShowProjectModal(true)} data-id="element-4812">
              Manage Options
            </Button>
          </div>
        </div>

        {/* KPI Cards - Row 1: Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4813">
          <Card data-id="element-4814">
            <CardContent className="p-6" data-id="element-4815">
              <div className="flex items-center gap-4" data-id="element-4816">
                <div className="p-3 bg-primary-50 rounded-lg flex-shrink-0" data-id="element-4817">
                  <Users className="h-6 w-6 text-primary-600" data-id="element-4818" />
                </div>
                <div className="min-w-0" data-id="element-4819">
                  <p className="text-sm font-medium text-slate-500" data-id="element-4820">
                    Active Buyers
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-4821">124</h3>
                  <p className="text-xs text-green-600 mt-1" data-id="element-4822">
                    <span className="font-medium" data-id="element-4823">+12%</span>
                    <span className="ml-1 text-slate-400" data-id="element-4824">vs last month</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-4825">
            <CardContent className="p-6" data-id="element-4826">
              <div className="flex items-center gap-4" data-id="element-4827">
                <div className="p-3 bg-emerald-50 rounded-lg flex-shrink-0" data-id="element-4828">
                  <DollarSign className="h-6 w-6 text-emerald-600" data-id="element-4829" />
                </div>
                <div className="min-w-0" data-id="element-4830">
                  <p className="text-sm font-medium text-slate-500" data-id="element-4831">
                    Upgrade Revenue
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-4832">$271K</h3>
                  <p className="text-xs text-green-600 mt-1" data-id="element-4833">
                    <span className="font-medium" data-id="element-4834">+18%</span>
                    <span className="ml-1 text-slate-400" data-id="element-4835">vs last month</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-4836">
            <CardContent className="p-6" data-id="element-4837">
              <div className="flex items-center gap-4" data-id="element-4838">
                <div className="p-3 bg-purple-50 rounded-lg flex-shrink-0" data-id="element-4839">
                  <TrendingUp className="h-6 w-6 text-purple-600" data-id="element-4840" />
                </div>
                <div className="min-w-0" data-id="element-4841">
                  <p className="text-sm font-medium text-slate-500" data-id="element-4842">
                    Avg Upgrade Value
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-4843">$45.2K</h3>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-4844">Per customer</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-4845">
            <CardContent className="p-6" data-id="element-4846">
              <div className="flex items-center gap-4" data-id="element-4847">
                <div className="p-3 bg-slate-100 rounded-lg flex-shrink-0" data-id="element-4848">
                  <Clock className="h-6 w-6 text-slate-600" data-id="element-4849" />
                </div>
                <div className="min-w-0" data-id="element-4850">
                  <p className="text-sm font-medium text-slate-500" data-id="element-4851">
                    Total Time Spent
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-4852">842h</h3>
                  <p className="text-xs text-green-600 mt-1" data-id="element-4853">
                    <span className="font-medium" data-id="element-4854">+5%</span>
                    <span className="ml-1 text-slate-400" data-id="element-4855">vs last month</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPI Cards - Row 2: Top Performers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4856">
          <Card data-id="element-4857">
            <CardContent className="p-6" data-id="element-4858">
              <div className="flex items-center gap-4" data-id="element-4859">
                <div className="p-3 bg-slate-100 rounded-lg flex-shrink-0" data-id="element-4860">
                  <Home className="h-6 w-6 text-slate-600" data-id="element-4861" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-4862">
                  <p className="text-sm font-medium text-slate-500" data-id="element-4863">
                    Top Model
                  </p>
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-4864">
                    The Lawson
                  </h3>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-4865">
                    45 active prospects
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-4866">
            <CardContent className="p-6" data-id="element-4867">
              <div className="flex items-center gap-4" data-id="element-4868">
                <div className="p-3 bg-slate-100 rounded-lg flex-shrink-0" data-id="element-4869">
                  <Layers className="h-6 w-6 text-slate-600" data-id="element-4870" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-4871">
                  <p className="text-sm font-medium text-slate-500" data-id="element-4872">
                    Top Option
                  </p>
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-4873">
                    Quartz Countertops
                  </h3>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-4874">
                    Selected by 82% of buyers
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4875">
          {/* Chart Section */}
          <Card className="lg:col-span-2" data-id="element-4876">
            <CardHeader data-id="element-4877">
              <CardTitle data-id="element-4878">Buyer Distribution by Model</CardTitle>
            </CardHeader>
            <CardContent data-id="element-4879">
              <div className="h-[300px] w-full" data-id="element-4880">
                <ResponsiveContainer width="100%" height="100%" data-id="element-4881">
                  <BarChart data={data} layout="vertical" margin={{
                  top: 5,
                  right: 30,
                  left: 40,
                  bottom: 5
                }} data-id="element-4882">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" data-id="element-4883" />
                    <XAxis type="number" hide data-id="element-4884" />
                    <YAxis dataKey="name" type="category" width={100} tick={{
                    fill: '#64748b',
                    fontSize: 12
                  }} axisLine={false} tickLine={false} data-id="element-4885" />
                    <Tooltip cursor={{
                    fill: '#f1f5f9'
                  }} contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} data-id="element-4886" />
                    <Bar dataKey="buyers" radius={[0, 4, 4, 0]} barSize={32} data-id="element-4887">
                      {data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-4888" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Status & Quick Actions */}
          <div className="space-y-6" data-id="element-4889">
            <Card data-id="element-4890">
              <CardHeader data-id="element-4891">
                <CardTitle data-id="element-4892">System Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-4893">
                <div className="flex items-center justify-between" data-id="element-4894">
                  <div className="flex items-center gap-3" data-id="element-4895">
                    <div className="h-2 w-2 rounded-full bg-green-500" data-id="element-4896"></div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-4897">
                      App Connection
                    </span>
                  </div>
                  <Badge variant="success" data-id="element-4898">Healthy</Badge>
                </div>
                <div className="flex items-center justify-between" data-id="element-4899">
                  <div className="flex items-center gap-3" data-id="element-4900">
                    <div className="h-2 w-2 rounded-full bg-green-500" data-id="element-4901"></div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-4902">
                      Options Sync
                    </span>
                  </div>
                  <span className="text-xs text-slate-500" data-id="element-4903">2 mins ago</span>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-4904">
                  <Button variant="outline" size="sm" className="w-full" leftIcon={<RefreshCw className="h-3 w-3" data-id="element-4906" />} data-id="element-4905">
                    Force Sync
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 text-white border-slate-800" data-id="element-4907">
              <CardContent className="p-6" data-id="element-4908">
                <h3 className="font-bold text-lg mb-2" data-id="element-4909">Need help?</h3>
                <p className="text-slate-400 text-sm mb-4" data-id="element-4910">
                  Our support team is available to help you configure your
                  project options.
                </p>
                <Button variant="secondary" className="w-full !bg-white !text-slate-900 hover:!bg-slate-100 !shadow-sm" onClick={() => navigate('/support')} data-id="element-4911">
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Select Project Modal */}
      {showProjectModal && <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-50" data-id="element-4912">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-4913">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-4914">
              <div data-id="element-4915">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-4916">
                  Select Project
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-4917">
                  Choose a project to manage its options
                </p>
              </div>
              <button onClick={() => {
            setShowProjectModal(false);
            setSelectedProject('');
          }} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-4918">
                <X className="h-5 w-5" data-id="element-4919" />
              </button>
            </div>
            <div className="p-6" data-id="element-4920">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4921">
                Project
              </label>
              <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-4922">
                <option value="" data-id="element-4923">Select a project...</option>
                {projects.map(project => <option key={project.id} value={project.id} data-id="element-4924">
                    {project.name}
                  </option>)}
              </select>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-4925">
              <Button variant="outline" onClick={() => {
            setShowProjectModal(false);
            setSelectedProject('');
          }} data-id="element-4926">
                Cancel
              </Button>
              <Button onClick={handleManageOptions} disabled={!selectedProject} data-id="element-4927">
                Continue
              </Button>
            </div>
          </div>
        </div>}
    </>;
}